import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SettingsTab from './SettingsTab';
import ImportTab from './ImportTab';
import TemplatesTab from './TemplatesTab';
import PromotionTab from './PromotionTab';
import TeacherDashboard from '../teacher/Dashboard';
import SuiviStage from '../teacher/SuiviStage';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

// ─── Modale édition utilisateur ─────────────────────────────────────────────

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    ine: user.ine || '',
    role: user.role || 'student',
    accountStatus: user.accountStatus || 'ACTIVE',
    dateNaissance: user.dateNaissance ? user.dateNaissance.split('T')[0] : '',
    adresse: user.adresse || '',
    emailParent: user.emailParent || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.put(`/admin/users/${user.id}`, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm('Réinitialiser le mot de passe à StageLink2026! ?')) return;
    await api.post(`/admin/users/${user.id}/reset-password`);
    setResetDone(true);
    setTimeout(() => setResetDone(false), 4000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800">
            {user.role === 'student' ? 'Fiche étudiant' : 'Modifier le compte'} — {user.firstName} {user.lastName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prénom</label>
              <input type="text" required value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
              <input type="text" required value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className={inputClass} />
            </div>
            {user.role === 'student' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">INE</label>
                  <input type="text" value={form.ine} onChange={e => setForm(p => ({ ...p, ine: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date de naissance</label>
                  <input type="date" value={form.dateNaissance} onChange={e => setForm(p => ({ ...p, dateNaissance: e.target.value }))} className={inputClass} />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
                  <input type="text" value={form.adresse} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))} className={inputClass} placeholder="ex: 12 rue des Lilas, 75001 Paris" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email parent</label>
                  <input type="email" value={form.emailParent} onChange={e => setForm(p => ({ ...p, emailParent: e.target.value }))} className={inputClass} />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rôle</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className={inputClass}>
                <option value="student">Étudiant</option>
                <option value="teacher">Enseignant</option>
                <option value="admin">Admin</option>
                <option value="tutor">Tuteur</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
              <select value={form.accountStatus} onChange={e => setForm(p => ({ ...p, accountStatus: e.target.value }))} className={inputClass}>
                <option value="ACTIVE">Actif</option>
                <option value="PENDING_EMAIL">En attente</option>
                <option value="DISABLED">Désactivé</option>
              </select>
            </div>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {resetDone && <p className="text-green-600 text-sm">Mot de passe réinitialisé : StageLink2026!</p>}
          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={resetPassword} className="text-xs text-gray-400 hover:text-gray-600 underline">
              Réinitialiser le mot de passe
            </button>
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg">
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
              <button type="button" onClick={onClose} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tab : Classes ───────────────────────────────────────────────────────────

function ClassesAdminTab() {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [students, setStudents] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', schoolYearId: '', teacherId: '' });
  const [formError, setFormError] = useState('');
  const [editStudent, setEditStudent] = useState(null);
  const [addStudentFor, setAddStudentFor] = useState(null);
  const [studentForm, setStudentForm] = useState({ civilite: '', firstName: '', lastName: '', ine: '', email: '', phone: '', dateNaissance: '', adresse: '', emailParent: '' });
  const [studentFormError, setStudentFormError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/classes'),
      api.get('/admin/users'),
      api.get('/admin/school-years'),
    ]).then(([c, u, y]) => {
      setClasses(c.data);
      setTeachers(u.data.filter(u => u.role === 'teacher'));
      setSchoolYears(y.data);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadStudents = async (classeId) => {
    try {
      const res = await api.get(`/classes/${classeId}/students`);
      setStudents(prev => ({ ...prev, [classeId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const toggle = (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    loadStudents(id);
  };

  const addTeacher = async (classeId, teacherId) => {
    if (!teacherId) return;
    try { await api.post(`/classes/${classeId}/teachers`, { teacherId }); load(); }
    catch (e) { console.error(e); }
  };

  const removeTeacher = async (classeId, teacherId) => {
    try { await api.delete(`/classes/${classeId}/teachers/${teacherId}`); load(); }
    catch (e) { console.error(e); }
  };

  const deleteClasse = async (id) => {
    if (!window.confirm('Supprimer cette classe ?')) return;
    try { await api.delete(`/classes/${id}`); load(); } catch (e) { console.error(e); }
  };

  const createStudent = async (e, classeId) => {
    e.preventDefault(); setStudentFormError('');
    try {
      const res = await api.post('/auth/register', { ...studentForm, role: 'student' });
      await api.post(`/classes/${classeId}/students`, { studentId: res.data.id });
      setAddStudentFor(null);
      setStudentForm({ civilite: '', firstName: '', lastName: '', ine: '', email: '', phone: '', dateNaissance: '', adresse: '', emailParent: '' });
      loadStudents(classeId);
    } catch (err) { setStudentFormError(err.response?.data?.error || 'Erreur'); }
  };

  const removeStudentFromClasse = async (s, classeId) => {
    if (!window.confirm(`Retirer ${s.firstName} ${s.lastName} de cette classe ? Ses conventions et son compte resteront intacts.`)) return;
    try {
      await api.delete(`/classes/${classeId}/students/${s.id}`);
      loadStudents(classeId);
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const deleteStudent = async (s, classeId) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${s.firstName} ${s.lastName} ?`)) return;
    try {
      await api.delete(`/admin/users/${s.id}`);
      loadStudents(classeId);
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const createClasse = async (e) => {
    e.preventDefault(); setFormError('');
    try {
      await api.post('/classes', { name: form.name, schoolYearId: form.schoolYearId, teacherId: form.teacherId || undefined });
      setShowForm(false); setForm({ name: '', schoolYearId: '', teacherId: '' }); load();
    } catch (err) { setFormError(err.response?.data?.error || 'Erreur'); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;

  return (
    <div>
      {editStudent && (
        <EditUserModal
          user={editStudent}
          onClose={() => setEditStudent(null)}
          onSaved={() => {
            setEditStudent(null);
            if (expanded) loadStudents(expanded);
          }}
        />
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{classes.length} classe(s)</p>
        <button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          + Nouvelle classe
        </button>
      </div>

      {showForm && (
        <form onSubmit={createClasse} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3">
          <h3 className="font-medium text-gray-800">Créer une classe</h3>
          {formError && <p className="text-red-600 text-sm">{formError}</p>}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
              <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="ex: BTS NDRC 1" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Année scolaire</label>
              <select required value={form.schoolYearId} onChange={e => setForm(p => ({ ...p, schoolYearId: e.target.value }))} className={inputClass}>
                <option value="">Choisir</option>
                {schoolYears.map(y => <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' ✓' : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">1er enseignant (optionnel)</label>
              <select value={form.teacherId} onChange={e => setForm(p => ({ ...p, teacherId: e.target.value }))} className={inputClass}>
                <option value="">Aucun</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Créer</button>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {classes.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
            Aucune classe — importez des étudiants ou créez une classe manuellement.
          </div>
        )}
        {classes.map(c => {
          const classeTeachers = c.teachers?.map(tc => tc.teacher) || [];
          const assignedIds = new Set(classeTeachers.map(t => t.id));
          const available = teachers.filter(t => !assignedIds.has(t.id));

          return (
            <div key={c.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 flex items-center gap-3">
                <button onClick={() => toggle(c.id)} className="flex-1 text-left min-w-0">
                  <p className="font-medium text-gray-800">{c.name}</p>
                  <p className="text-xs text-gray-400">{c.schoolYear?.label} · {c.students?.length || 0} étudiant(s)</p>
                </button>

                {/* Enseignants affectés */}
                <div className="flex items-center gap-1.5 flex-wrap max-w-xs">
                  {classeTeachers.map(t => (
                    <span key={t.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                      {t.firstName} {t.lastName}
                      <button onClick={() => removeTeacher(c.id, t.id)} className="hover:text-red-500 leading-none">×</button>
                    </span>
                  ))}
                  {available.length > 0 && (
                    <select
                      defaultValue=""
                      onChange={e => { addTeacher(c.id, e.target.value); e.target.value = ''; }}
                      className="text-xs border border-dashed border-gray-300 rounded-full px-2 py-1 text-gray-500 focus:outline-none"
                    >
                      <option value="">+ Enseignant</option>
                      {available.map(t => <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>)}
                    </select>
                  )}
                </div>

                <button onClick={() => toggle(c.id)} className="text-gray-400 w-4 shrink-0 text-center">
                  {expanded === c.id ? '▲' : '▼'}
                </button>
                <button onClick={() => deleteClasse(c.id)} className="text-xs text-red-400 hover:text-red-600 shrink-0">Suppr.</button>
              </div>

              {expanded === c.id && (
                <div className="border-t border-gray-100 px-5 py-4 space-y-3">
                  {!students[c.id] ? (
                    <p className="text-sm text-gray-400">Chargement...</p>
                  ) : students[c.id].length === 0 ? (
                    <p className="text-sm text-gray-500">Aucun étudiant</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                          <th className="pb-2">Prénom</th>
                          <th className="pb-2">Nom</th>
                          <th className="pb-2">Email</th>
                          <th className="pb-2">INE</th>
                          <th className="pb-2">Statut</th>
                          <th className="pb-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {students[c.id].map(s => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="py-1.5 text-gray-700">{s.firstName}</td>
                            <td className="py-1.5 font-medium text-gray-800">{s.lastName}</td>
                            <td className="py-1.5 text-gray-500">{s.email || <span className="text-gray-300 italic">sans email</span>}</td>
                            <td className="py-1.5 text-gray-400 text-xs">{s.ine}</td>
                            <td className="py-1.5">
                              {s.tempPassword && (
                                <span className="text-xs bg-yellow-50 text-yellow-600 px-1.5 py-0.5 rounded">MDP temp.</span>
                              )}
                            </td>
                            <td className="py-1.5 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <button onClick={() => setEditStudent(s)} className="text-xs text-blue-600 hover:text-blue-800">Fiche</button>
                                <button onClick={() => removeStudentFromClasse(s, c.id)} className="text-xs text-orange-500 hover:text-orange-700">Retirer</button>
                                <button onClick={() => deleteStudent(s, c.id)} className="text-xs text-red-400 hover:text-red-600">Supprimer</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {addStudentFor === c.id ? (
                    <form onSubmit={e => createStudent(e, c.id)} className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-gray-700">Nouvel étudiant</p>
                      {studentFormError && <p className="text-red-600 text-xs">{studentFormError}</p>}
                      <div className="grid grid-cols-2 gap-2">
                        <select value={studentForm.civilite} onChange={e => setStudentForm(p => ({ ...p, civilite: e.target.value }))} className={inputClass}>
                          <option value="">Civilité</option>
                          <option value="M.">M.</option>
                          <option value="Mme">Mme</option>
                        </select>
                        <div />
                        <input required placeholder="Prénom" value={studentForm.firstName} onChange={e => setStudentForm(p => ({ ...p, firstName: e.target.value }))} className={inputClass} />
                        <input required placeholder="Nom" value={studentForm.lastName} onChange={e => setStudentForm(p => ({ ...p, lastName: e.target.value }))} className={inputClass} />
                        <input required placeholder="INE" value={studentForm.ine} onChange={e => setStudentForm(p => ({ ...p, ine: e.target.value }))} className={inputClass} />
                        <input placeholder="Email (optionnel)" value={studentForm.email} onChange={e => setStudentForm(p => ({ ...p, email: e.target.value }))} className={inputClass} />
                        <input placeholder="Téléphone (optionnel)" value={studentForm.phone} onChange={e => setStudentForm(p => ({ ...p, phone: e.target.value }))} className={inputClass} />
                        <input type="date" placeholder="Date de naissance (optionnel)" value={studentForm.dateNaissance} onChange={e => setStudentForm(p => ({ ...p, dateNaissance: e.target.value }))} className={inputClass} />
                        <input placeholder="Adresse (optionnel)" value={studentForm.adresse} onChange={e => setStudentForm(p => ({ ...p, adresse: e.target.value }))} className={`${inputClass} col-span-2`} />
                        <input type="email" placeholder="Email parent (optionnel, mineur)" value={studentForm.emailParent} onChange={e => setStudentForm(p => ({ ...p, emailParent: e.target.value }))} className={`${inputClass} col-span-2`} />
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-lg">Créer</button>
                        <button type="button" onClick={() => { setAddStudentFor(null); setStudentFormError(''); }} className="text-xs text-gray-500 px-2">Annuler</button>
                      </div>
                    </form>
                  ) : (
                    <button onClick={() => { setAddStudentFor(c.id); setStudentForm({ firstName: '', lastName: '', ine: '', email: '', phone: '', dateNaissance: '' }); setStudentFormError(''); }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      + Ajouter un élève
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tab : Enseignants ───────────────────────────────────────────────────────

function EnseignantsTab() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    api.get('/admin/users')
      .then(r => setTeachers(r.data.filter(u => u.role === 'teacher')))
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deleteTeacher = async (t) => {
    if (!window.confirm(`Supprimer ${t.firstName} ${t.lastName} ?`)) return;
    try {
      await api.delete(`/admin/users/${t.id}`);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const createTeacher = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/auth/register', { ...form, role: 'teacher' });
      setSuccess('Enseignant créé — un email avec le mot de passe temporaire a été envoyé.');
      setShowCreate(false); setForm({ firstName: '', lastName: '', email: '', phone: '' }); load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;

  return (
    <div>
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{teachers.length} enseignant(s)</p>
        <button onClick={() => setShowCreate(!showCreate)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          + Nouvel enseignant
        </button>
      </div>

      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">{success}</div>}

      {showCreate && (
        <form onSubmit={createTeacher} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3">
          <h3 className="font-medium text-gray-800">Créer un compte enseignant</h3>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Prénom *</label><input type="text" required value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label><input type="text" required value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email *</label><input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label><input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className={inputClass} /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Créer</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Prénom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Nom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Téléphone</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {teachers.map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-700">{t.firstName}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{t.lastName}</td>
                <td className="px-4 py-3 text-gray-500">{t.email}</td>
                <td className="px-4 py-3 text-gray-500">{t.phone || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.accountStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {t.accountStatus === 'ACTIVE' ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right flex items-center justify-end gap-3">
                  <button onClick={() => setEditUser(t)} className="text-xs text-blue-600 hover:text-blue-800">Modifier</button>
                  <button onClick={() => deleteTeacher(t)} className="text-xs text-red-400 hover:text-red-600">Supprimer</button>
                </td>
              </tr>
            ))}
            {teachers.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun enseignant</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab : Années scolaires ──────────────────────────────────────────────────

const fmt = d => new Date(d).toLocaleDateString('fr-FR');

function PeriodeManager({ classe }) {
  const [periodes, setPeriodes] = useState(classe.periodes || []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', startDate: '', endDate: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ nom: p.nom, startDate: p.startDate.split('T')[0], endDate: p.endDate.split('T')[0] });
    setShowForm(false);
  };

  const cancelEdit = () => { setEditId(null); setForm({ nom: '', startDate: '', endDate: '' }); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (editId) {
        const res = await api.put(`/classes/${classe.id}/periodes/${editId}`, form);
        setPeriodes(prev => prev.map(p => p.id === editId ? res.data : p));
        setEditId(null);
      } else {
        const res = await api.post(`/classes/${classe.id}/periodes`, form);
        setPeriodes(prev => [...prev, res.data]);
        setShowForm(false);
      }
      setForm({ nom: '', startDate: '', endDate: '' });
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette période ?')) return;
    try {
      await api.delete(`/classes/${classe.id}/periodes/${id}`);
      setPeriodes(prev => prev.filter(p => p.id !== id));
    } catch { alert('Erreur lors de la suppression'); }
  };

  const formFields = (
    <form onSubmit={save} className="flex gap-2 flex-wrap items-end mt-2">
      {error && <p className="w-full text-red-600 text-xs">{error}</p>}
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Nom</label>
        <input required type="text" value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
          placeholder="ex: Stage 1" className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-28" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Début</label>
        <input required type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Fin</label>
        <input required type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-lg">
        {saving ? '...' : editId ? 'Enregistrer' : 'Ajouter'}
      </button>
      <button type="button" onClick={editId ? cancelEdit : () => setShowForm(false)} className="text-xs text-gray-500 px-2 py-1.5">Annuler</button>
    </form>
  );

  return (
    <div className="px-4 py-3">
      {periodes.length === 0 && !showForm && (
        <p className="text-xs text-gray-400 mb-2">Aucune période définie</p>
      )}
      {periodes.length > 0 && (
        <div className="space-y-1 mb-2">
          {periodes.map(p => (
            <div key={p.id}>
              {editId === p.id ? formFields : (
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-medium text-gray-700 w-20">{p.nom}</span>
                  <span className="text-gray-500">{fmt(p.startDate)} → {fmt(p.endDate)}</span>
                  <button onClick={() => startEdit(p)} className="text-blue-500 hover:text-blue-700">Modifier</button>
                  <button onClick={() => remove(p.id)} className="text-red-400 hover:text-red-600">Suppr.</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!editId && (showForm ? formFields : (
        <button onClick={() => setShowForm(true)} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Ajouter une période</button>
      ))}
    </div>
  );
}

function SchoolYearsTab() {
  const [years, setYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ label: '', startDate: '', endDate: '', isActive: false });
  const [error, setError] = useState('');
  const [expandedClasse, setExpandedClasse] = useState(null);

  const load = () => Promise.all([
    api.get('/admin/school-years'),
    api.get('/classes'),
  ]).then(([y, c]) => { setYears(y.data); setClasses(c.data); }).catch(console.error);

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/admin/school-years', form);
      setForm({ label: '', startDate: '', endDate: '', isActive: false }); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Années scolaires */}
      <div>
        <form onSubmit={create} className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-medium text-gray-800 mb-4">Ajouter une année scolaire</h3>
          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
          <div className="flex gap-3 flex-wrap items-end">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Libellé</label>
              <input type="text" placeholder="2025-2026" required value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Début</label>
              <input type="date" required value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Fin</label>
              <input type="date" required value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} className="rounded" />Active
            </label>
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Ajouter</button>
          </div>
        </form>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-3">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Libellé</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Début</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Fin</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {years.map(y => (
                <tr key={y.id}>
                  <td className="px-4 py-3 font-medium">{y.label}</td>
                  <td className="px-4 py-3 text-gray-500">{fmt(y.startDate)}</td>
                  <td className="px-4 py-3 text-gray-500">{fmt(y.endDate)}</td>
                  <td className="px-4 py-3">{y.isActive && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Périodes de stage par classe */}
      <div>
        <h3 className="font-medium text-gray-800 mb-1">Périodes de stage par classe</h3>
        <p className="text-xs text-gray-500 mb-3">Définissez les périodes de stage (Stage 1, Stage 2…) pour chaque classe. Les étudiants pourront créer une convention par période.</p>
        {classes.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune classe disponible.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {classes.map((c, i) => (
              <div key={c.id} className={i > 0 ? 'border-t border-gray-100' : ''}>
                <button
                  onClick={() => setExpandedClasse(expandedClasse === c.id ? null : c.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <div>
                    <span className="font-medium text-sm text-gray-800">{c.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{c.schoolYear?.label}</span>
                    <span className="text-xs text-gray-400 ml-2">· {c.periodes?.length || 0} période(s)</span>
                  </div>
                  <span className="text-gray-400 text-xs">{expandedClasse === c.id ? '▲' : '▼'}</span>
                </button>
                {expandedClasse === c.id && <PeriodeManager key={c.id} classe={c} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab : Paramètres ────────────────────────────────────────────────────────

function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    api.get('/admin/users').then(r => setAdmins(r.data.filter(u => u.role === 'admin'))).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createAdmin = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/auth/register', { ...form, role: 'admin' });
      setSuccess('Compte admin créé (mot de passe : StageLink2026!)');
      setShowCreate(false); setForm({ firstName: '', lastName: '', email: '' }); load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-4">Chargement...</div>;

  return (
    <div>
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{admins.length} administrateur(s)</p>
        <button onClick={() => setShowCreate(!showCreate)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">+ Nouvel admin</button>
      </div>
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">{success}</div>}
      {showCreate && (
        <form onSubmit={createAdmin} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3">
          <h3 className="font-medium text-gray-800">Créer un compte administrateur</h3>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Prénom *</label><input type="text" required value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label><input type="text" required value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email *</label><input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inputClass} /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Créer</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
          </div>
        </form>
      )}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Prénom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Nom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {admins.map(a => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-700">{a.firstName}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{a.lastName}</td>
                <td className="px-4 py-3 text-gray-500">{a.email}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditUser(a)} className="text-xs text-blue-600 hover:text-blue-800">Modifier</button>
                </td>
              </tr>
            ))}
            {admins.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Aucun administrateur</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ParametresTab() {
  const [sub, setSub] = useState('etablissement');
  return (
    <div>
      <div className="flex gap-4 mb-6 border-b border-gray-100">
        {[{ id: 'etablissement', label: 'Établissement' }, { id: 'templates', label: 'Templates' }, { id: 'admins', label: 'Administrateurs' }, { id: 'personnel', label: 'Personnel' }].map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`pb-2 px-1 text-xs font-medium border-b-2 transition-colors ${sub === t.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'etablissement' && <SettingsTab />}
      {sub === 'templates' && <TemplatesTab />}
      {sub === 'admins' && <AdminsTab />}
      {sub === 'personnel' && <PersonnelTab />}
    </div>
  );
}

// ─── Tab : Personnel ─────────────────────────────────────────────────────────

function PersonnelTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    api.get('/admin/users').then(r => setList(r.data.filter(u => u.role === 'personnel'))).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer ${u.firstName} ${u.lastName} ?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const createPersonnel = async (e) => {
    e.preventDefault(); setError('');
    try {
      await api.post('/auth/register', { ...form, role: 'personnel' });
      setSuccess('Compte personnel créé (mot de passe temporaire envoyé)');
      setShowCreate(false); setForm({ firstName: '', lastName: '', email: '', phone: '' }); load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-4">Chargement...</div>;

  return (
    <div>
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{list.length} compte(s) personnel</p>
        <button onClick={() => setShowCreate(!showCreate)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">+ Nouveau compte</button>
      </div>
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4">{success}</div>}
      {showCreate && (
        <form onSubmit={createPersonnel} className="bg-white rounded-xl border border-gray-200 p-5 mb-4 space-y-3">
          <h3 className="font-medium text-gray-800">Créer un compte personnel (proviseur adjoint…)</h3>
          <p className="text-xs text-gray-500">Accès en lecture aux conventions et au suivi des stages.</p>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Prénom *</label><input type="text" required value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label><input type="text" required value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Email *</label><input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inputClass} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label><input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className={inputClass} /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg">Créer</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-sm text-gray-600 px-3 py-2">Annuler</button>
          </div>
        </form>
      )}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Prénom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Nom</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {list.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-700">{u.firstName}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{u.lastName}</td>
                <td className="px-4 py-3 text-gray-500">{u.email}</td>
                <td className="px-4 py-3 text-right flex items-center justify-end gap-3">
                  <button onClick={() => setEditUser(u)} className="text-xs text-blue-600 hover:text-blue-800">Modifier</button>
                  <button onClick={() => deleteUser(u)} className="text-xs text-red-400 hover:text-red-600">Supprimer</button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Aucun compte personnel</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab : À propos ──────────────────────────────────────────────────────────

function AProposTab() {
  return (
    <div className="max-w-md">
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Application</p>
          <div className="space-y-2 text-sm">
            <div className="flex gap-2"><span className="text-gray-500 w-20">Version</span><span className="font-medium text-gray-800">1.0 Beta</span></div>
          </div>
        </div>
        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Auteur</p>
          <div className="space-y-2 text-sm">
            <div className="flex gap-2"><span className="text-gray-500 w-20">Nom</span><span className="font-medium text-gray-800">Heurteloup Yacine</span></div>
            <div className="flex gap-2"><span className="text-gray-500 w-20">Contact</span><a href="mailto:contact@edudeploy.fr" className="text-blue-600 hover:underline">contact@edudeploy.fr</a></div>
          </div>
        </div>
        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400">Edudeploy — Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
}

// ─── AdminPanel principal ────────────────────────────────────────────────────

const TABS = [
  { id: 'conventions',  label: 'Conventions' },
  { id: 'suivi',        label: 'Suivi des stages' },
  { id: 'classes',      label: 'Classes' },
  { id: 'enseignants',  label: 'Enseignants' },
  { id: 'school-years', label: 'Années scolaires' },
  { id: 'promotion',    label: 'Promotion' },
  { id: 'import',       label: 'Import CSV' },
  { id: 'parametres',   label: 'Paramètres' },
  { id: 'apropos',      label: 'À propos' },
];

const PERSONNEL_TABS = ['conventions', 'suivi', 'apropos'];

export default function AdminPanel() {
  const { user } = useAuth();
  const isPersonnel = user?.role === 'personnel';
  const [tab, setTab] = useState('conventions');
  const visibleTabs = isPersonnel ? TABS.filter(t => PERSONNEL_TABS.includes(t.id)) : TABS;

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Administration</h2>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {visibleTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'conventions'  && <TeacherDashboard />}
      {tab === 'suivi'        && <SuiviStage readOnly={isPersonnel} />}
      {tab === 'classes'      && <ClassesAdminTab />}
      {tab === 'enseignants'  && <EnseignantsTab />}
      {tab === 'school-years' && <SchoolYearsTab />}
      {tab === 'promotion'    && <PromotionTab />}
      {tab === 'import'       && <ImportTab />}
      {tab === 'parametres'   && <ParametresTab />}
      {tab === 'apropos'      && <AProposTab />}
    </div>
  );
}
